import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import EmailIcon from '../../../../../images/icon_email_outline_gray.svg'
import { VerifyEvery60SecondsContent } from '../../../../molecules/verifyEvery60SecondsContent'
import { useTranslation } from '../../../../../context/translationProvider'
import {
  doSendValidationEmail,
  doValidateEmail,
} from '../../../../../adapters/auth'
import { useFeatureValue } from '@growthbook/growthbook-react'
import { toast } from '../../../../atoms'
import { removeNationalIdMask } from '../../../../../helpers/mask'
import { RegisterChangeValueContent } from '../../customComponents/registerChangeValueContent'
import { Field } from '../../../../molecules/field'
import { emailRegex } from '../../../../../utils/emailValidation'
import { useReCaptcha } from '../../../../../hooks/useReCaptcha'
import { formSubmitIsDisabled } from '../../../../../utils/formUtils'
import { AlreadyDoneContent } from '../../customComponents/alreadyDoneContent'
import { useEventTracking } from '../../../../../context/eventTrackingProvider'
import { TurnstileInvisible, useTurnstileInvisibleOn } from '../../../../../hooks/useTurnstileInvisible'

const EmailVerificationStep = (props) => {
  const {
    formProperties,
    onCompleteStep,
    onInputFocus,
    onInputBlur,
    stepIsDone,
  } = props

  const {
    register,
    getValues,
    trigger,
    formState: { errors },
  } = formProperties

  const { translate } = useTranslation()
  const duration = useFeatureValue('registration_timer_to_resend')
  const email = getValues('email')
  const [isChangingValue, setIsChangingValue] = useState()
  const [isTurnstile, setIsTurnstile] = useState(false)
  const [OTP, setOTP] = useState('')
  const [inputLoading, setInputLoading] = useState(false)
  const {captchaTurnstileOn} = useTurnstileInvisibleOn()
  const { trackEvent, EVENT_NAMES, EVENT_LOCATIONS } = useEventTracking()
  const emailCodeLength = useFeatureValue('fe_igp_email_verification_code_length')

  useEffect(() => {
    if (isChangingValue) {
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'registrationw_email_edit_page_viewed',
        })
      }
    } else {
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'registrationw_email_page_viewed',
        })
      }
    }
  }, [isChangingValue])

  const onResend = async ({ captcha_token, captcha_solution }) => {
    const email = getValues('email')
    const languageCode = getValues('languageCode')
    const national_id = getValues('national_id')
    const { ok, error } = await doSendValidationEmail({
      email,
      national_id: removeNationalIdMask(national_id),
      language_code: languageCode,
      captcha_token,
      captcha_solution
    })

    if (!ok) {
      switch (error.messageCode) {
        case 186:
          toast.error(
            translate(
              'register.error.the_username_already_taken_please_select_another'
            )
          )
          break
        case 420:
          if (!isTurnstile && captchaTurnstileOn) {
            setIsTurnstile(true)
            onResendTurnstile()

            break
          } 
          toast.error(translate('registration.error.email.sending'))
          break
        default:
          toast.error(translate('registration.error.email.sending'))
          break
      }
    }

    setIsChangingValue(false)
  }

  const onResendRecaptcha = useReCaptcha(async ({ recaptchaToken }) => {
    return await onResend({ captcha_token: recaptchaToken, captcha_solution: 'RECAPTCHA' });
  }, 'EMAIL_VERIFICATION');

  const onResendTurnstile = () => {
    window.turnstile.reset('#turnstileCaptchaSend')
    window.turnstile.execute('#turnstileCaptchaSend')
  }

  const onInputFieldDone = async ({ verification_code, captcha_token, captcha_solution}) => {
      const email = getValues('email')
      const national_id = getValues('national_id')

      const { ok, error = {} } = await doValidateEmail({
        email,
        national_id: removeNationalIdMask(national_id),
        token: verification_code,
        captcha_token,
        captcha_solution
      })
      if (!ok) {
        switch (error.messageCode) {
          case 32:
            trackEvent(EVENT_NAMES.EMAIL_INVALID_CODE_RETURNED)
            setInputLoading(false)
            return toast.error(translate('register.error.email.wrongCode'))
          case 420:
            if (captcha_solution !== 'TURNSTILE' && captchaTurnstileOn) {
              onInputFieldDoneTurnstile()
              setInputLoading(true)
              return
            }

            setInputLoading(false)
            toast.error(translate('registration.error.email.sending'))
            return
          default:
            setInputLoading(false) 
            return toast.error(translate('registration.error.email.validating'))
        }
      }

      setInputLoading(false)
      return onCompleteStep()
    }

  const onInputFieldDoneRecaptcha = useReCaptcha(
    async ({ verification_code, recaptchaToken }) => {
      return await onInputFieldDone({
        verification_code,
        captcha_token: recaptchaToken,
        captcha_solution: 'RECAPTCHA'
      });
    },
    'EMAIL_VERIFICATION'
  );

  const onInputFieldDoneTurnstile = () => {
    window.turnstile.reset('#turnstileCaptchaVerify')
    window.turnstile.execute('#turnstileCaptchaVerify', {
      callback: async (data) => {
        onInputFieldDone({
          captcha_token: data, 
          captcha_solution: 'TURNSTILE_INVISIBLE',
          verification_code: OTP
        })
      }
    })
  }

  const onChangeEmail = async () => {
    const isValid = await trigger('email')
    if (!isValid) return

    isTurnstile ? onResendTurnstile() : onResendRecaptcha()
  }

  const didDoChangeValueBefore = isChangingValue === false

  if (stepIsDone) {
    return (
      <AlreadyDoneContent
        icon={EmailIcon}
        title={translate('register.alreadyDone.email.title')}
        description={translate('register.alreadyDone.email.description')}
        onNext={() => onCompleteStep()}
        style={{ paddingTop: '1em', justifyContent: 'start' }}
      />
    )
  }

  if (isChangingValue) {
    return (
      <RegisterChangeValueContent
        onSubmit={onChangeEmail}
        icon={EmailIcon}
        ctaText={translate('verification.email.changeValue.button')}
        title={translate('verification.email.changeValue.title')}
        description={translate('verification.email.changeValue.description')}
        formSubmitIsDisabled={
          formSubmitIsDisabled(
            ['email'], 
            getValues,
            errors,
          )
        }
        renderField={() => (
          <Field
            label={translate('userProfile.email')}
            description={translate('userProfile.emailHint')}
            maxLength="100"
            type="email"
            autoCompleteOff
            showErrorMsg
            errors={errors}
            onFocus={onInputFocus}
            onBlur={() => onInputBlur('email_input_timer')}
            formProperties={register('email', {
              required: true,
              pattern: {
                value: emailRegex,
                message: translate('register.error.incorrectEmailFormat'),
              },
            })}
          />
        )}
        style={{ paddingTop: '1em', justifyContent: 'start' }}
      />
    )
  }

  return (
    <>
      <TurnstileInvisible
        action={'EMAIL_VERIFICATION'}
        execution={'execute'}
        id={'turnstileCaptchaSend'}
        onCaptchaVerified={(data) => {
          onResend({ 
            captcha_token: data, 
            captcha_solution: 'TURNSTILE_INVISIBLE' 
          })
        }}
      />

       <TurnstileInvisible
        action={'EMAIL_VERIFICATION'}
        execution={'execute'}
        id={'turnstileCaptchaVerify'}
      />
      
      <VerifyEvery60SecondsContent
        inputFieldNumOfChars={emailCodeLength}
        useResendOnStart={!didDoChangeValueBefore}
        useTimerToEnableResendOnStart
        onResend={isTurnstile ? onResendTurnstile : onResendRecaptcha}
        icon={EmailIcon}
        onInputFieldDone={onInputFieldDoneRecaptcha}
        duration={duration}
        ctaText={translate('verification.email.buttonText')}
        fullLineClickable
        title={translate('verification.email.title')}
        fieldLabel={translate('verification.email.fieldLabel')}
        onChangeValueClicked={() => setIsChangingValue(true)}
        description={translate('verification.email.description').replaceAll(
          '{email}.',
        `</br><strong>${email}</strong>.</br>`
        )}
        tickingText={translate('verification.email.60seconds')}
        tickingTextBold
        didntReceiveText={translate('verification.email.clickBelow')}
        onRender={() =>
          trackEvent(EVENT_NAMES.SCREEN_LOADED, {
            screen_name: EVENT_LOCATIONS.REGISTER_EMAIL_VERIFICATION,
          })
        }
        style={{ paddingTop: '1em', justifyContent: 'start' }}
        getInputFieldValue={setOTP}
        altInputLoading={inputLoading}
        textCenter
      />
    </>
  )
}

EmailVerificationStep.defaultProps = {
  formProperties: {},
  onCompleteStep: () => {},
  onInputFocus: () => {},
  onInputBlur: () => {},
  stepIsDone: false,
}

EmailVerificationStep.propTypes = {
  formProperties: PropTypes.object,
  onCompleteStep: PropTypes.func,
  onInputFocus: PropTypes.func,
  onInputBlur: PropTypes.func,
  stepIsDone: PropTypes.bool,
}

export { EmailVerificationStep }
